/**
 * Form fields: the label, the control, and the message under it.
 *
 * The reason these are one component and not three is accessibility, and it is
 * not a style choice. A label has to be tied to its input with `htmlFor`, and an
 * error message has to be tied to the input with `aria-describedby` — otherwise
 * a screen reader announces "Quantity, edit, blank" and never the sentence
 * saying why the save is refused. Getting that right by hand at every field is
 * exactly the sort of thing that is right on nineteen fields and wrong on the
 * twentieth.
 *
 * So `Field` owns the wiring and takes the control as a child. It is impossible
 * to render a labelled field with this component and forget the id.
 */
import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

const CONTROL =
  'w-full rounded border bg-slate-900 px-2.5 py-2 text-sm text-slate-100 placeholder:text-slate-500 ' +
  'focus:outline-none focus:border-brand-500 disabled:opacity-50';

function borderFor(invalid: boolean, className?: string): string {
  return cn(CONTROL, invalid ? 'border-red-600' : 'border-slate-700', className);
}

export interface FieldProps {
  /** The visible label. Also the accessible name. */
  readonly label: string;
  /** Shown under the control. The error wins if both are given. */
  readonly hint?: string;
  readonly error?: string;
  readonly required?: boolean;
  /** Rendered to the right of the label, e.g. a unit or a lookup button. */
  readonly adornment?: ReactNode;
  readonly className?: string;
  readonly children: (props: {
    id: string;
    'aria-invalid': boolean | undefined;
    'aria-describedby': string | undefined;
    'aria-required': boolean | undefined;
  }) => ReactNode;
}

export function Field({ label, hint, error, required, adornment, className, children }: FieldProps) {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;
  const describedBy = message ? messageId : undefined;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-xs font-medium uppercase tracking-wide text-slate-400">
          {label}
          {required ? <span className="ml-1 text-red-400">*</span> : null}
        </label>
        {adornment}
      </div>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })}
      {message ? (
        <p
          id={messageId}
          // `role="alert"` on an error so it is announced the moment it appears,
          // which matters when the reason a save is blocked was not visible.
          role={error ? 'alert' : undefined}
          className={cn('text-xs', error ? 'text-red-300' : 'text-slate-500')}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & { readonly invalid?: boolean };

export function Input({ className, invalid, ...rest }: InputProps) {
  return <input {...rest} aria-invalid={invalid || undefined} className={borderFor(Boolean(invalid), className)} />;
}

export type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & { readonly invalid?: boolean };

export function Textarea({ className, invalid, rows = 3, ...rest }: TextareaProps) {
  return (
    <textarea
      {...rest}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(borderFor(Boolean(invalid), className), 'resize-y')}
    />
  );
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  readonly invalid?: boolean;
  readonly options: readonly SelectOption[];
  /** Shown as a disabled first option, so an unset field is visibly unset. */
  readonly placeholder?: string;
  readonly children?: ReactNode;
}

export function Select({ className, invalid, options, placeholder, children, ...rest }: SelectProps) {
  return (
    <select {...rest} aria-invalid={invalid || undefined} className={borderFor(Boolean(invalid), className)}>
      {placeholder !== undefined ? (
        <option value="" disabled>
          {placeholder}
        </option>
      ) : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
      {children}
    </select>
  );
}

/** A labelled text input. The common case, wired correctly by construction. */
export function TextField({
  label,
  hint,
  error,
  required,
  className,
  ...rest
}: Omit<InputProps, 'id'> & { label: string; hint?: string; error?: string; wrapperClassName?: string }) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {(wiring) => <Input {...rest} {...wiring} invalid={Boolean(error)} />}
    </Field>
  );
}
